import React, {CSSProperties, ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {makeStyles, TableCell} from "@material-ui/core";
import classNames from "classnames";
import {TableCellProps} from "@material-ui/core/TableCell";
import CustomizedTooltip from "../Tooltip/Tooltip";

const useStyles = makeStyles(() => ({
    cell: {
        overflow: 'hidden',
        minWidth: 0,
    },
    ellipsis: {
        display: 'block',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        lineHeight: '1.4'
    },
}));

type CustomTableCellProps = TableCellProps & {
    children?: ReactNode;
    className?: string;
    style?: CSSProperties;
    disableHover?: boolean;
    disableEllipsis?: boolean;
};

const CustomTableCell: React.FC<CustomTableCellProps> = ({ children, className, style, disableHover = false, disableEllipsis = false, ...rest }) => {
    const classes = useStyles();
    const ref = useRef<HTMLElement | null>(null);
    const [isEllipsisActive, setIsEllipsisActive] = useState(false);
    const isEllipsisActiveRef = useRef(false);
    const debounceRef = useRef<number | null>(null);

    const checkEllipsis = useCallback(() => {
        if (disableEllipsis) return;

        const el = ref.current;
        if (!el) return;

        const hasEllipsis = el.scrollWidth > el.clientWidth;

        // Only update state if value actually changed
        if (hasEllipsis !== isEllipsisActiveRef.current) {
            isEllipsisActiveRef.current = hasEllipsis;
            setIsEllipsisActive(hasEllipsis);
        }
    }, [disableEllipsis]);

    const debouncedCheck = useCallback(() => {
        if (debounceRef.current) {
            cancelAnimationFrame(debounceRef.current);
        }
        debounceRef.current = requestAnimationFrame(checkEllipsis);
    }, [checkEllipsis]);

    useEffect(() => {
        if (disableEllipsis) return;

        const el = ref.current;
        if (!el) return;

        // Initial check with delay to ensure layout is complete
        const timeout = setTimeout(checkEllipsis, 50);

        let resizeObserver: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(debouncedCheck);
            resizeObserver.observe(el);
        } else {
            window.addEventListener('resize', debouncedCheck);
        }

        return () => {
            clearTimeout(timeout);
            if (debounceRef.current) {
                cancelAnimationFrame(debounceRef.current);
            }
            if (resizeObserver && el) resizeObserver.unobserve(el);
            if (!resizeObserver) window.removeEventListener('resize', debouncedCheck);
        };
    }, [children, style, checkEllipsis, debouncedCheck, disableEllipsis]);

    // If ellipsis is disabled, render without wrapper
    if (disableEllipsis) {
        return (
            <TableCell
                className={classNames(classes.cell, className)}
                style={style}
                {...rest}
            >
                {children}
            </TableCell>
        );
    }

    return (
        <CustomizedTooltip
            disableHoverListener={!isEllipsisActive || disableHover}
            title={children}
            copy={false}
        >
            <TableCell
                className={classNames(classes.cell, className)}
                style={style}
                {...rest}
            >
                <span ref={ref} className={classes.ellipsis}>
                    {children}
                </span>
            </TableCell>
        </CustomizedTooltip>
    );
};

export default CustomTableCell;
